import * as React from "react"

import { cn } from "../lib/utils"

interface TableProps extends React.ComponentProps<"table"> {
  /**
   * Wrap the table in the DS card chrome (rounded border + white background).
   *
   * Off by default on purpose. Most hand-rolled tables in the app sit inside a
   * Card or an expanded row, where a second border doubles the chrome — that
   * is exactly why they could not adopt `DataTable`, which always draws it.
   */
  bordered?: boolean
  /** Class names for the scroll container rather than the `<table>`. */
  containerClassName?: string
}

/**
 * Low-level table primitives carrying the same visual vocabulary as
 * {@link DataTable}, minus its opinionated container, loading/empty states and
 * column config. Use `DataTable` for a plain list of records; reach for these
 * when the table needs a custom row component, sticky columns, editable cells
 * or row expansion.
 */
const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerClassName, bordered = false, ...props }, ref) => (
    <div
      data-slot="table-container"
      className={cn(
        "overflow-x-auto",
        bordered && "overflow-hidden rounded-lg border border-neutral-200 bg-white",
        containerClassName
      )}
    >
      <table
        ref={ref}
        data-slot="table"
        className={cn("min-w-full divide-y divide-neutral-200", className)}
        {...props}
      />
    </div>
  )
)
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentProps<"thead">
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    data-slot="table-header"
    className={cn("bg-neutral-50", className)}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentProps<"tbody">
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    data-slot="table-body"
    className={cn("divide-y divide-neutral-200 bg-white", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.ComponentProps<"tfoot">
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    data-slot="table-footer"
    className={cn(
      "border-t border-neutral-200 bg-neutral-50 font-medium",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.ComponentProps<"tr">
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    data-slot="table-row"
    className={cn(
      "transition-colors hover:bg-neutral-50 data-[state=selected]:bg-primary-50",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentProps<"th">
>(({ className, scope = "col", ...props }, ref) => (
  <th
    ref={ref}
    scope={scope}
    data-slot="table-head"
    className={cn(
      "px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.ComponentProps<"td">
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    data-slot="table-cell"
    className={cn(
      "whitespace-nowrap px-4 py-3 text-sm text-neutral-900",
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.ComponentProps<"caption">
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    data-slot="table-caption"
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
}
export type { TableProps }
